/**
 * Pinyin syllable → `ZH_I_*` / `ZH_F_*` phone codes + tone class + 十三辙.
 *
 * Phone namespace follows the §12.1/§12.2 manifest examples
 * (`ZH_I_S`, `ZH_F_I`, `ZH_F_IANG`, …). Conventions resolved here:
 *   - orthographic abbreviations are expanded to their phonological form:
 *     `iu` → `ZH_F_IOU`, `ui` → `ZH_F_UEI`, `un` → `ZH_F_UEN` (`ün` → `ZH_F_VN`);
 *   - `ü` is written `V` in codes (`ZH_F_V`, `ZH_F_VE`, `ZH_F_VAN`, `ZH_F_VN`);
 *   - the apical vowel `-i` after z/c/s/zh/ch/sh/r keeps `ZH_F_I` (per §12.1 四 →
 *     `["ZH_I_S","ZH_F_I"]`) and is flagged via `apical`.
 *
 * 红线 14.5: this module is a *helper for material authoring only*. Material rows
 * must carry `pinyin_locked`; G2P fallback from 汉字 is forbidden.
 */
import type { Zhe } from "./types.js";

/** The 21 initials (声母). */
export const INITIALS = [
  "b",
  "p",
  "m",
  "f",
  "d",
  "t",
  "n",
  "l",
  "g",
  "k",
  "h",
  "j",
  "q",
  "x",
  "zh",
  "ch",
  "sh",
  "r",
  "z",
  "c",
  "s",
] as const;
export type Initial = (typeof INITIALS)[number];

export const INITIAL_CODES: Record<Initial, string> = Object.fromEntries(
  INITIALS.map((i) => [i, `ZH_I_${i.toUpperCase()}`]),
) as Record<Initial, string>;

/** Canonical (phonological) finals 韵母. `v` = ü. */
export const FINALS = [
  "a",
  "o",
  "e",
  "er",
  "ai",
  "ei",
  "ao",
  "ou",
  "an",
  "en",
  "ang",
  "eng",
  "ong",
  "i",
  "ia",
  "ie",
  "iao",
  "iou",
  "ian",
  "in",
  "iang",
  "ing",
  "iong",
  "u",
  "ua",
  "uo",
  "uai",
  "uei",
  "uan",
  "uen",
  "uang",
  "ueng",
  "v",
  "ve",
  "van",
  "vn",
] as const;
export type Final = (typeof FINALS)[number];

export const FINAL_CODES: Record<Final, string> = Object.fromEntries(
  FINALS.map((f) => [f, `ZH_F_${f.toUpperCase()}`]),
) as Record<Final, string>;

/** 附录 A 十三辙 (草案表) — canonical final → 辙名. */
export const ZHE_TABLE: Record<Final, Zhe> = {
  a: "发花",
  ia: "发花",
  ua: "发花",
  o: "梭波",
  e: "梭波",
  uo: "梭波",
  ie: "乜斜",
  ve: "乜斜",
  i: "一七",
  v: "一七",
  er: "一七",
  u: "姑苏",
  en: "人辰",
  in: "人辰",
  uen: "人辰",
  vn: "人辰",
  eng: "中东",
  ing: "中东",
  ong: "中东",
  iong: "中东",
  ueng: "中东",
  ai: "怀来",
  uai: "怀来",
  ei: "灰堆",
  uei: "灰堆",
  ao: "遥条",
  iao: "遥条",
  ou: "由求",
  iou: "由求",
  an: "言前",
  ian: "言前",
  uan: "言前",
  van: "言前",
  ang: "江阳",
  iang: "江阳",
  uang: "江阳",
};

/** Finals grouped by 辙 (inverse of {@link ZHE_TABLE}). */
export const ZHE_FINALS: Record<string, Final[]> = (() => {
  const out: Record<string, Final[]> = {};
  for (const f of FINALS) {
    const z = ZHE_TABLE[f];
    (out[z] ??= []).push(f);
  }
  return out;
})();

export interface PhoneParse {
  syllable: string;
  /** `ZH_I_*` or `null` for zero-initial (y/w/∅) syllables. */
  initial: string | null;
  /** `ZH_F_*` — always present for a valid syllable. */
  final: string;
  /** 0 = neutral, 1–4 = 阴平/阳平/上/去. */
  tone: number;
  zhe: Zhe;
  /** true for the apical vowel -i after z/c/s/zh/ch/sh/r ([ɿ]/[ʅ]). */
  apical: boolean;
  /** Canonical phone sequence, initial first. Zero-initial → one entry. */
  phones: string[];
}

const TONE_MARKS: Record<string, [string, number]> = {
  ā: ["a", 1],
  á: ["a", 2],
  ǎ: ["a", 3],
  à: ["a", 4],
  ō: ["o", 1],
  ó: ["o", 2],
  ǒ: ["o", 3],
  ò: ["o", 4],
  ē: ["e", 1],
  é: ["e", 2],
  ě: ["e", 3],
  è: ["e", 4],
  ī: ["i", 1],
  í: ["i", 2],
  ǐ: ["i", 3],
  ì: ["i", 4],
  ū: ["u", 1],
  ú: ["u", 2],
  ǔ: ["u", 3],
  ù: ["u", 4],
  ǖ: ["v", 1],
  ǘ: ["v", 2],
  ǚ: ["v", 3],
  ǜ: ["v", 4],
  ü: ["v", 0],
  ń: ["n", 2],
  ň: ["n", 3],
  ǹ: ["n", 4],
};

/** Zero-initial (y-/w-) spellings → canonical final. */
const ZERO_INITIAL: Record<string, Final> = {
  yi: "i",
  ya: "ia",
  ye: "ie",
  yao: "iao",
  you: "iou",
  yan: "ian",
  yin: "in",
  yang: "iang",
  ying: "ing",
  yong: "iong",
  yo: "o",
  yu: "v",
  yue: "ve",
  yuan: "van",
  yun: "vn",
  wu: "u",
  wa: "ua",
  wo: "uo",
  wai: "uai",
  wei: "uei",
  wan: "uan",
  wen: "uen",
  wang: "uang",
  weng: "ueng",
  a: "a",
  o: "o",
  e: "e",
  ai: "ai",
  ei: "ei",
  ao: "ao",
  ou: "ou",
  an: "an",
  en: "en",
  ang: "ang",
  eng: "eng",
  er: "er",
  /**
   * 呼读音 only. `ong` is not a free-standing syllable in Hanyu Pinyin, but the
   * 韵母表 is read aloud as itself in 正音教学 (AI 正音测试 一级 · 音素), and the
   * material row for that final has to carry a parseable `pinyin_locked`.
   */
  ong: "ong",
};

/** Orthographic final after an initial → canonical final. */
const FINAL_ALIAS: Record<string, Final> = {
  iu: "iou",
  ui: "uei",
  un: "uen",
  ü: "v",
  üe: "ve",
  üan: "van",
  ün: "vn",
  u: "u",
  ue: "ve", // nüe/lüe sometimes written nue/lue
};

const APICAL_INITIALS = new Set(["z", "c", "s", "zh", "ch", "sh", "r"]);
const JQX = new Set(["j", "q", "x"]);

export class PinyinError extends Error {}

/** Strip tone marks / trailing tone digits. Returns [base, tone|null]. */
export function stripTone(raw: string): [string, number | null] {
  let s = raw
    .normalize("NFC")
    .trim()
    .toLowerCase()
    .replace(/['’\s]/g, "");
  let tone: number | null = null;
  const m = /^([a-zü:]+)([0-5])$/i.exec(s);
  if (m) {
    s = m[1]!;
    const t = Number(m[2]);
    tone = t === 5 ? 0 : t;
  }
  let out = "";
  for (const ch of s) {
    const hit = TONE_MARKS[ch];
    if (hit) {
      out += hit[0];
      if (hit[1] > 0) tone = hit[1];
    } else {
      out += ch;
    }
  }
  out = out.replace(/u:/g, "v").replace(/ü/g, "v");
  return [out, tone];
}

/**
 * Parse one pinyin syllable (tone number or tone mark) into phone codes.
 * Throws {@link PinyinError} on an unparseable syllable.
 */
export function parsePinyin(raw: string): PhoneParse {
  const [base, toneRaw] = stripTone(raw);
  if (!base) throw new PinyinError(`empty pinyin: ${JSON.stringify(raw)}`);
  const tone = toneRaw ?? 0;

  // zero-initial (y-/w-/bare vowel)
  const zero = ZERO_INITIAL[base];
  if (zero) return build(raw, null, zero, tone, false);

  // longest-match initial (zh/ch/sh before z/c/s)
  let initial: Initial | null = null;
  for (const cand of ["zh", "ch", "sh", ...INITIALS] as string[]) {
    if (base.startsWith(cand) && base.length > cand.length) {
      initial = cand as Initial;
      break;
    }
  }
  if (!initial) throw new PinyinError(`unparseable pinyin syllable: ${JSON.stringify(raw)}`);

  let rest = base.slice(initial.length);
  // j/q/x + u… is always ü
  if (JQX.has(initial)) {
    if (rest === "u") rest = "v";
    else if (rest === "ue") rest = "ve";
    else if (rest === "uan") rest = "van";
    else if (rest === "un") rest = "vn";
  }
  const canonical = (FINAL_ALIAS[rest] ?? rest) as Final;
  if (!(canonical in ZHE_TABLE)) {
    throw new PinyinError(`unknown final ${JSON.stringify(rest)} in ${JSON.stringify(raw)}`);
  }
  const apical = canonical === "i" && APICAL_INITIALS.has(initial);
  return build(raw, initial, canonical, tone, apical);
}

function build(
  syllable: string,
  initial: Initial | null,
  final: Final,
  tone: number,
  apical: boolean,
): PhoneParse {
  const initialCode = initial ? INITIAL_CODES[initial] : null;
  const finalCode = FINAL_CODES[final];
  return {
    syllable,
    initial: initialCode,
    final: finalCode,
    tone,
    zhe: ZHE_TABLE[final],
    apical,
    phones: initialCode ? [initialCode, finalCode] : [finalCode],
  };
}

/** Non-throwing variant. */
export function tryParsePinyin(raw: string): PhoneParse | null {
  try {
    return parsePinyin(raw);
  } catch {
    return null;
  }
}

const MARKED: Record<string, string[]> = {
  a: ["a", "ā", "á", "ǎ", "à"],
  o: ["o", "ō", "ó", "ǒ", "ò"],
  e: ["e", "ē", "é", "ě", "è"],
  i: ["i", "ī", "í", "ǐ", "ì"],
  u: ["u", "ū", "ú", "ǔ", "ù"],
  ü: ["ü", "ǖ", "ǘ", "ǚ", "ǜ"],
};

/**
 * Display form of a locked pinyin syllable: `si4` → `sì`, `nv3` → `nǚ`,
 * `lve4` → `lüè`, `ma5`/`ma0` → `ma`. Unparseable input is returned unchanged.
 * Tone-mark placement: a/e first; `ou` marks o; otherwise the last vowel.
 */
export function toToneMarks(raw: string): string {
  const [base, tone] = stripTone(raw);
  if (!base) return raw;
  let s = base.replace(/v/g, "ü");
  // j/q/x/y + u are ü in codes but written u in display
  if (/^(j|q|x|y)ü/.test(s)) s = s.replace(/^([jqxy])ü/, "$1u");
  if (tone === null || tone === 0) return s;
  const vowels = ["a", "e", "o", "i", "u", "ü"];
  let idx = -1;
  if (s.includes("a")) idx = s.indexOf("a");
  else if (s.includes("e")) idx = s.indexOf("e");
  else if (s.includes("ou")) idx = s.indexOf("o");
  else {
    for (let k = s.length - 1; k >= 0; k--) {
      if (vowels.includes(s[k]!)) {
        idx = k;
        break;
      }
    }
  }
  if (idx < 0) return s;
  const v = s[idx]!;
  const marked = MARKED[v]?.[tone];
  if (!marked) return s;
  return s.slice(0, idx) + marked + s.slice(idx + 1);
}

/** Display form of a whitespace-separated locked-pinyin sequence. */
export function toToneMarksSequence(text: string): string {
  return text.trim().split(/\s+/).filter(Boolean).map(toToneMarks).join(" ");
}

/** Split a whitespace-separated locked-pinyin string into per-syllable parses. */
export function parsePinyinSequence(text: string): PhoneParse[] {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => parsePinyin(s));
}
